import React, { useState, useEffect } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { useAuth } from '../../context/AuthContext';
import { muxAPI } from '../../services/api';

const MuxVideoPlayer = ({ video, onError }) => {
  const { user } = useAuth();
  const [playbackData, setPlaybackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPlaybackToken();
  }, [video.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlaybackToken = async () => {
    try {
      setLoading(true);
      const response = await muxAPI.getPlaybackToken(video.id);
      setPlaybackData(response.data);
    } catch (err) {
      if (err.response?.status === 402) {
        setError('purchase_required');
      } else {
        setError(err.response?.data?.error || 'Failed to load video');
      }
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="aspect-video bg-black flex items-center justify-center rounded-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error === 'purchase_required') {
    return (
      <div className="aspect-video bg-black flex flex-col items-center justify-center rounded-lg">
        <div className="text-white text-2xl font-bold mb-4">Premium Content</div>
        <p className="text-gray-400 mb-6">Purchase access to watch this battle</p>
        <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-colors">
          Buy Access
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aspect-video bg-black flex items-center justify-center rounded-lg">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  const streamType = video.metadata?.is_live ? 'live' : 'on-demand';

  return (
    <div className="relative rounded-lg overflow-hidden">
      <MuxPlayer
        playbackId={playbackData.playback_id}
        tokens={{
          playback: playbackData.playback_token,
          thumbnail: playbackData.thumbnail_token,
        }}
        streamType={streamType}
        metadata={{
          video_id: video.id,
          video_title: video.title,
          viewer_user_id: user?.id,
        }}
        primaryColor="#ef4444"
        secondaryColor="#000000"
        accentColor="#ef4444"
        style={{ aspectRatio: '16/9', width: '100%' }}
        autoPlay={false}
      />

      {/* Watermark overlay - anti-piracy */}
      {user && (
        <WatermarkOverlay username={user.username} />
      )}
    </div>
  );
};

// Anti-piracy watermark that shows username in random positions
const WatermarkOverlay = ({ username }) => {
  const [position, setPosition] = useState({ top: '10%', left: '10%' });

  useEffect(() => {
    const positions = [
      { top: '10%', left: '10%' },
      { top: '10%', right: '10%', left: 'auto' },
      { bottom: '15%', left: '10%', top: 'auto' },
      { bottom: '15%', right: '10%', top: 'auto', left: 'auto' },
      { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    ];

    const interval = setInterval(() => {
      const randomPos = positions[Math.floor(Math.random() * positions.length)];
      setPosition(randomPos);
    }, 45000); // Change position every 45 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="absolute pointer-events-none select-none transition-all duration-1000"
      style={{
        ...position,
        color: 'rgba(255, 255, 255, 0.08)',
        fontSize: '14px',
        fontFamily: 'monospace',
        zIndex: 10,
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {username}
    </div>
  );
};

export default MuxVideoPlayer;
